import { useState } from "react";
import { View, Share, Platform, ImageBackground, Text } from "react-native";
import { Gift, Share2, X } from "lucide-react-native";
import { usePostHog } from "posthog-react-native";
import { GlassButton, IconButton } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { useDismissible } from "@src/packages/homepage/utils/useDismissible";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  REFERRAL_CAMPAIGN_ID,
  REFERRAL_ORIGIN,
  buildReferralUrl,
  referralCodeForPatient,
} from "../../../../referral/referralLink";
import ShareSheet from "./ShareSheet";

const careHeroGlowGradient =
  require("@src/apps/moon/assets/images/care-hero-glow-gradient.png") as number;

/** Two overlapping circles mirror the partner prompt while signaling sharing. */
function InlineInviteFriendVisual() {
  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <View
        style={{
          width: 30,
          height: 30,
          borderRadius: 15,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255,247,239,0.16)",
          borderWidth: 1.5,
          borderColor: "rgba(255,247,239,0.28)",
          zIndex: 1,
        }}
      >
        <Share2 size={12} color="rgba(255,247,239,0.94)" strokeWidth={1.7} />
      </View>

      <View
        style={{
          width: 30,
          height: 30,
          borderRadius: 15,
          alignItems: "center",
          justifyContent: "center",
          marginLeft: -8,
          backgroundColor: "rgba(21,10,7,0.08)",
          borderWidth: 1.5,
          borderColor: "rgba(255,247,239,0.38)",
          borderStyle: "dashed",
        }}
      >
        <Gift size={12} color="rgba(255,247,239,0.94)" strokeWidth={1.7} />
      </View>
    </View>
  );
}

function InlineInviteFriendCard({ onShare }: { onShare: () => void }) {
  const t = useTranslations();
  const { isDismissed, dismiss } = useDismissible("you/help-a-friend-card");

  if (isDismissed) return null;

  return (
    <View style={{ flex: 1, minWidth: 0, marginBottom: 16 }}>
      <ImageBackground
        source={careHeroGlowGradient}
        resizeMode="cover"
        imageStyle={{ borderRadius: 16 }}
        style={{
          minHeight: 198,
          overflow: "hidden",
          borderRadius: 16,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.16)",
        }}
      >
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(21, 10, 7, 0.1)",
          }}
        />

        <View style={{ flex: 1, padding: 14 }}>
          <View style={{ position: "absolute", top: 8, right: 8 }}>
            <IconButton
              accessibilityLabel={t("you.invite_friend.dismiss")}
              onPress={dismiss}
              size="sm"
              surface="soft"
              backgroundColor="rgba(255,255,255,0.1)"
              pressedBackgroundColor="rgba(255,255,255,0.18)"
            >
              <X size={14} color="rgba(255,247,239,0.82)" strokeWidth={1.8} />
            </IconButton>
          </View>

          <InlineInviteFriendVisual />

          <Text
            className="font-dm-sans"
            numberOfLines={2}
            style={{
              marginTop: 10,
              paddingRight: 22,
              fontSize: 15,
              lineHeight: 19,
              fontWeight: "600",
              color: "#FFFFFF",
            }}
          >
            {t("you.invite_friend.section_title")}
          </Text>

          <Text
            className="font-dm-sans"
            numberOfLines={3}
            style={{
              flex: 1,
              marginTop: 6,
              fontSize: 12,
              lineHeight: 16,
              fontWeight: "500",
              color: "rgba(255,247,239,0.78)",
            }}
          >
            {t("you.invite_friend.compact_blurb")}
          </Text>

          <GlassButton
            label={t("you.invite_friend.share_button")}
            onPress={onShare}
            leading={
              <Share2
                size={13}
                color="rgba(255,247,239,0.9)"
                strokeWidth={1.9}
              />
            }
            style={{ alignSelf: "stretch" }}
            textStyle={{ fontSize: 12 }}
          />
        </View>
      </ImageBackground>
    </View>
  );
}

export default function InviteFriend({
  compact = false,
}: {
  compact?: boolean;
}) {
  const t = useTranslations();
  const posthog = usePostHog();
  const { patientId } = useApiClient();
  const [sheetVisible, setSheetVisible] = useState(false);

  // Per-user link so we can track which user sent an invite (and who they bring
  // in). Falls back to the bare invite URL if the patient id isn't available
  // yet, so the button always works.
  const referralCode = patientId ? referralCodeForPatient(patientId) : null;
  const inviteUrl = referralCode
    ? buildReferralUrl(referralCode)
    : `${REFERRAL_ORIGIN}/invite`;

  const message = t("you.invite_friend.share_message", {
    params: { url: inviteUrl },
  });
  const title = t("you.invite_friend.share_title");

  const shareEventProps = {
    code: referralCode,
    inviter_patient_id: patientId ?? null,
    campaign_id: REFERRAL_CAMPAIGN_ID,
  };

  const handleShare = async () => {
    // Sharing is the "win a free cycle" entry, so this event doubles as the
    // sweepstakes entry signal.
    posthog.capture("invite_friend_shared", {
      surface: Platform.OS,
      ...shareEventProps,
    });

    if (Platform.OS === "web") {
      setSheetVisible(true);
      return;
    }

    try {
      await Share.share({ message, url: inviteUrl, title });
    } catch {
      // User cancelled — no-op
    }
  };

  const handleChannelSelect = (channel: string) => {
    posthog.capture("invite_friend_channel_selected", {
      channel,
      ...shareEventProps,
    });
  };

  const shareSheet = (
    <ShareSheet
      visible={sheetVisible}
      url={inviteUrl}
      onChannelSelect={handleChannelSelect}
      onClose={() => setSheetVisible(false)}
    />
  );

  if (compact) {
    return (
      <>
        <InlineInviteFriendCard onShare={handleShare} />
        {shareSheet}
      </>
    );
  }

  return (
    <View>
      <Hero
        backgroundSource={careHeroGlowGradient}
        title={t("you.invite_friend.section_title")}
        summary={t("you.invite_friend.blurb")}
        // Match the shared 16px glass-card radius of the adjacent You cards.
        style={{ marginBottom: 20, borderRadius: 16 }}
        bottomContent={
          <View style={{ marginTop: 14, alignSelf: "flex-start" }}>
            <GlassButton
              label={t("you.invite_friend.share_button")}
              onPress={handleShare}
              leading={
                <Share2
                  size={15}
                  color="rgba(255,247,239,0.9)"
                  strokeWidth={1.9}
                />
              }
              style={{ paddingHorizontal: 18 }}
            />
          </View>
        }
      />

      {shareSheet}
    </View>
  );
}
